import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../clients/apiClient';

export interface PendingApplication {
  id: string;
  role: string | null;
  company: string | null;
  domain: string | null;
  created_at: string | null;
  outreach_status: string;
}

interface PendingRaw {
  id: string;
  role: string | null;
  outreach_status: string;
  created_at: string | null;
  companies: { name: string | null; domain: string | null } | null;
}

async function fetchPending(): Promise<PendingApplication[]> {
  const { data } = await apiClient.get<PendingRaw[]>('/api/automation/pending');
  return data.map((a) => ({
    id: a.id,
    role: a.role,
    company: a.companies?.name ?? null,
    domain: a.companies?.domain ?? null,
    created_at: a.created_at,
    outreach_status: a.outreach_status,
  }));
}

export function useAutomationQueue() {
  const queryClient = useQueryClient();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const query = useQuery<PendingApplication[]>({
    queryKey: ['automation-pending'],
    queryFn: fetchPending,
    refetchInterval: 30_000,
  });

  const approve = async (applicationId: string, roleOverride?: string, maxOutreach?: number, sendMode?: 'draft' | 'send', attachResume?: boolean) => {
    setLoadingId(applicationId);
    try {
      await apiClient.post(`/api/automation/${applicationId}/approve`, {
        role: roleOverride || undefined,
        max_outreach: maxOutreach,
        send_mode: sendMode || 'draft',
        attach_resume: attachResume ?? false,
      });
      queryClient.setQueryData<PendingApplication[]>(['automation-pending'], (prev) =>
        (prev ?? []).filter((a) => a.id !== applicationId)
      );
      queryClient.invalidateQueries({ queryKey: ['pipeline'] });
    } finally {
      setLoadingId(null);
    }
  };

  const ignore = async (applicationId: string) => {
    setLoadingId(applicationId);
    try {
      await apiClient.post(`/api/automation/${applicationId}/ignore`);
      queryClient.setQueryData<PendingApplication[]>(['automation-pending'], (prev) =>
        (prev ?? []).filter((a) => a.id !== applicationId)
      );
    } finally {
      setLoadingId(null);
    }
  };

  return { ...query, approve, ignore, loadingId };
}
